import React, {
  cloneElement,
  createElement,
  Fragment,
  isValidElement,
  useCallback,
  useEffect,
  useState,
} from 'react'

import { TableCell, TableRow, Checkbox } from '@material-ui/core'
import { makeStyles } from '@material-ui/styles'
import classnames from 'classnames'
import {
  useExpanded,
  linkToRecord,
  DatagridCell,
  ExpandRowButton,
} from 'react-admin'
import { useHistory } from 'react-router'

import { orangeSemitransparent } from '../../constants/colorConstants'

const useStyles = makeStyles({
  selected: {
    backgroundColor: orangeSemitransparent,
  },
})

export const defaultClasses = { expandIconCell: '', checkbox: '', rowCell: '' }

export const computeNbColumns = (expand, children, hasBulkActions) =>
  expand
    ? 1 + // show expand button
      (hasBulkActions ? 1 : 0) + // checkbox column
      React.Children.toArray(children).filter((child) => !!child).length // non-null children
    : 0 // we don't need to compute columns if there is no expand panel;

export const CustomDatagridRowCreditLine = React.forwardRef(
  (
    {
      basePath,
      children,
      classes = defaultClasses,
      className,
      expand,
      hasBulkActions,
      hover,
      id,
      onToggleItem,
      record,
      resource,
      rowClick,
      selected,
      style,
      selectable,
      currentSort,
      defaultTitle,
      displayedFilters,
      filterValues,
      hasCreate,
      hideFilter,
      loading,
      loaded,
      onUnselectItems,
      perPage,
      selectedIds,
      setFilters,
      setPage,
      setPerPage,
      setSort,
      showFilter,
      totalPages,
      exporter,
      hasList,
      hasEdit,
      hasShow,
      onUserExpand,
      onUserCollapse,
      ...rest
    },
    ref
  ) => {
    const customClasses = useStyles()
    const searchParams = new URLSearchParams(
      window.location.href.substring(window.location.href.indexOf('?') + 1)
    )
    const selectedItemId = searchParams.get('selectedItemId')

    const [expanded, toggleExpanded] = useExpanded(resource, id)
    const [nbColumns, setNbColumns] = useState(
      computeNbColumns(expand, children, hasBulkActions)
    )
    useEffect(() => {
      // Fields can be hidden dynamically based on permissions;
      // The expand panel must span over the remaining columns
      // So we must recompute the number of columns to span on
      const newNbColumns = computeNbColumns(expand, children, hasBulkActions)
      if (newNbColumns !== nbColumns) {
        setNbColumns(newNbColumns)
      }
    }, [expand, nbColumns, children, hasBulkActions])
    useEffect(() => {
      if (selectedItemId !== undefined && record.id === selectedItemId) {
        if (!expanded) {
          toggleExpanded()
          if (onUserExpand) onUserExpand(record)
        }
      }
    })

    const history = useHistory()

    const handleToggleExpand = useCallback(
      (event) => {
        if (!expanded && onUserExpand) onUserExpand(record)
        if (expanded && onUserCollapse) onUserCollapse(record)
        toggleExpanded()
        event.stopPropagation()
      },
      [toggleExpanded, expanded, onUserExpand, onUserCollapse, record]
    )
    const handleToggleSelection = useCallback(
      (event) => {
        if (!selectable) return
        onToggleItem(id)
        event.stopPropagation()
      },
      [id, onToggleItem, selectable]
    )
    const handleClick = useCallback(
      async (event) => {
        console.log(rowClick)
        if (!rowClick) return
        event.persist()

        const effect =
          typeof rowClick === 'function'
            ? await rowClick(id, basePath, record)
            : rowClick
        switch (effect) {
          case 'edit':
            history.push(linkToRecord(basePath, id))
            return
          case 'show':
            history.push(linkToRecord(basePath, id, 'show'))
            return
          case 'expand':
            handleToggleExpand(event)
            return
          case 'toggleSelection':
            handleToggleSelection(event)
            return
          default:
            if (effect) history.push(effect)
            return
        }
      },
      [
        basePath,
        history,
        handleToggleExpand,
        handleToggleSelection,
        id,
        record,
        rowClick,
      ]
    )

    return (
      <Fragment>
        <TableRow
          ref={ref}
          className={classnames(
            className,
            selectedItemId !== undefined && record.id === selectedItemId
              ? customClasses.selected
              : undefined
          )}
          key={id}
          style={style}
          hover={hover}
          onClick={handleClick}
          {...rest}
        >
          {expand && (
            <TableCell padding="none" className={classes.expandIconCell}>
              <ExpandRowButton
                classes={classes}
                expanded={expanded}
                onClick={handleToggleExpand}
                expandContentId={`${id}-expand`}
              />
            </TableCell>
          )}
          {hasBulkActions && (
            <TableCell padding="checkbox">
              {selectable && (
                <Checkbox
                  color="primary"
                  className={`select-item ${classes.checkbox}`}
                  checked={selected}
                  onClick={handleToggleSelection}
                />
              )}
            </TableCell>
          )}
          {React.Children.map(children, (field, index) =>
            isValidElement(field) ? (
              <DatagridCell
                key={`${id}-${field.props.source || index}`}
                className={classnames(
                  `column-${field.props.source}`,
                  classes.rowCell
                )}
                record={record}
                {...{ field, basePath, resource }}
              />
            ) : null
          )}
        </TableRow>
        {expand && expanded && (
          <TableRow key={`${id}-expand`} id={`${id}-expand`}>
            <TableCell colSpan={nbColumns}>
              {isValidElement(expand)
                ? cloneElement(expand, {
                    // @ts-ignore
                    record,
                    basePath,
                    resource,
                    id: String(id),
                  })
                : createElement(expand, {
                    record,
                    basePath,
                    resource,
                    id: String(id),
                  })}
            </TableCell>
          </TableRow>
        )}
      </Fragment>
    )
  }
)
